import { ScrollingModule } from "@angular/cdk/scrolling";
import { ApiService } from "../../services/api.service";
import { MaterialModule } from "../../modules/material/material.module";
import {
  ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter,
  forwardRef,
  HostListener,
  Injector,
  Input,
  Output,

  QueryList, ViewChild,
  ViewChildren
} from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from "rxjs/operators";
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl
} from "@angular/forms";
import { FilterCondition, TableListRequest } from "../../models/table-list-request.interface";
import { OrderBy } from "../../models/user-list-request.interface";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import { ToasterService } from "../../services/toaster.service";
import { CommonModule, KeyValuePipe } from "@angular/common";
import { CommonUtils } from "../../utils/common-utils";
import { NzEmptyComponent } from "ng-zorro-antd/empty";
import { NzTagComponent } from "ng-zorro-antd/tag";
import { SpinnerLoaderComponent } from "../spinner-loader/spinner-loader.component";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";
import { DynamicFormService } from "../../services/dynamic-form.service";
import { IdName } from "../../models/id-name.interface";
import { GridTableService } from "../../services/grid-table.service";
import { NoDataFoundComponent } from "../no-data-found/no-data-found.component";

@Component({
  selector: "app-autocomplete-dropdown",
  standalone: true,
  imports: [
    CommonModule,
    ScrollingModule,
    MaterialModule,
    SvgIconComponent,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    FormsModule,
    NzEmptyComponent,
    NzTagComponent,
    SpinnerLoaderComponent,
    NoDataFoundComponent
  ],
  templateUrl: "./autocomplete-dropdown.component.html",
  styleUrl: "./autocomplete-dropdown.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteDropdownComponent),
      multi: true
    }
  ]
})
export class AutocompleteDropdownComponent implements ControlValueAccessor, ICellEditorAngularComp {
  @Input() apiUrl: string = "";
  @Input() selectFields: string[] = [];
  @Input() filterByKeys: string[] = [];
  @Input() filterByKey: string = ""; //deprecated
  @Input() limit: number = 10;
  @Input() orderBy: OrderBy = {};
  @Input() placeholder: string = "Select";
  @Input() formControl!: FormControl;
  @Input() bindLabel: string = "";
  @Input() bindLabelFallbacks?: string[] = [];
  @Input() id: string = "";
  @Input() bindValue: string = "";
  @Input() skipIds: string[] = [];
  @Input() disable: boolean = false;
  @Input() readonly: boolean = false;
  @Input() additionalFilters: FilterCondition[] = [];
  @Input() displayColumns: Array<{ key: string; header: string; dataType?: string }> = [];
  @Input() returnRawValue: boolean = true;
  @Input() allowCustomInput: boolean = false;
  @Input() clientSideSearch: boolean = false;
  @Input() clientSearchData: any[] = [];
  @Input() showIcon: boolean = true;
  @Input() multiSelect: boolean = false;
  @Input() searchUsingExpression: boolean = false; //used for determinants search where search string is required in the expression
  @Input() maxVisiblePills: number = 5;
  @Input() allowFullSearch: boolean = true;

  @Output() searchCleared = new EventEmitter<void>();
  @Output() suggestionSelected = new EventEmitter<any>();
  @Output() customInputSelected = new EventEmitter<string>();

  @ViewChild("trigger") trigger!: CdkOverlayOrigin;
  @ViewChild("resultsContainer") resultsContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren("optionRow") optionRows!: QueryList<ElementRef>;
  @ViewChild("searchInput") searchInput!: ElementRef<HTMLInputElement>;

  value: any;
  onChange: (value: any) => void = () => {
  };
  onTouched: () => void = () => {
  };

  private params!: any;

  isDisabled: boolean = false;
  searchTerm: string = "";
  previousTerm: string = "";
  focusSearch: boolean = false;
  suggestions: any[] = [];
  showSuggestions = false;
  page = 0;
  canPullMoreData: boolean = false;
  loading = true;
  searchTermSubject: Subject<string> = new Subject<string>();
  selectedItems: Map<string, any> = new Map();
  triggerWidth = 0;
  overlayOffsetX = 0;
  activeOptionIndex: number = -1;

  searchListRequest: TableListRequest = {
    selectFields: [],
    orderBy: this.orderBy,
    limit: this.limit ?? 10,
    page: this.page ?? 0
  };
  maxVisibleItems: number = this.limit ?? 10;
  overlayPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 2      // 8px gap *below* the trigger
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -2      // 8px gap *above* the trigger
    }
  ];
  ngControl: NgControl | null = null;

  private static readonly EMPTY_SEARCH_RESULT = {
    term: "",
    records: [] as any[],
    hideSuggestions: true
  };
  private static readonly SCROLL_THRESHOLD_PX = 100;

  @HostListener("window:resize")
  onResize() {
    this.updateWidth();
  }

  constructor(private apiService: ApiService,
              protected cdr: ChangeDetectorRef,
              private toasterService: ToasterService,
              private injector: Injector,
              private dynamicFormService: DynamicFormService,
              private fb: FormBuilder,
              private gridTableService: GridTableService) {
  }

  ngAfterViewInit() {
    this.updateWidth();
    this.cdr.detectChanges();
  }

  private updateWidth() {
    const triggerElement = this.trigger?.elementRef?.nativeElement as HTMLElement | undefined;
    if (!triggerElement) {
      return;
    }

    const headerSearchBar = triggerElement.closest(".header_content .search_bar") as HTMLElement | null;
    if (headerSearchBar) {
      const triggerRect = triggerElement.getBoundingClientRect();
      const headerSearchBarRect = headerSearchBar.getBoundingClientRect();

      this.triggerWidth = Math.round(headerSearchBarRect.width);
      this.overlayOffsetX = Math.round(headerSearchBarRect.left - triggerRect.left);
      return;
    }

    this.overlayOffsetX = 0;
    this.triggerWidth = Math.round(triggerElement.getBoundingClientRect().width || triggerElement.offsetWidth || 0);
  }

  // Set initial value from form control
  writeValue(object: any): void {
    if (this.multiSelect) {
      this.writeValuesForMultiSelect(object ?? []);
    } else {
      this.writeValueForSingleSelect(object);
    }
  }

  reset() {
    this.value = null;
    this.searchTerm = "";
    this.selectedItems.clear();
    if (this.multiSelect) {
      this.updateMultiSelectValues();
    } else {
      this.onChange(null);
    }
    this.showSuggestions = false;
    this.validateNow();
  }

  private writeValueForSingleSelect(object: any) {
    console.log("pacthing in auto: ", object);
    if (object && typeof object === "object") {
      if (object.hasOwnProperty("key")) {
        this.value = object?.key ?? "";
        this.searchTerm = object.value || this.value;
      } else if (object.hasOwnProperty("id")) {
        this.value = object?.id ?? "";
        this.searchTerm = object?.name ?? "";
      } else {
        this.value = object[this.bindValue] ?? "";
        this.searchTerm = this.resolveDisplayLabel(object) || this.value;
      }
    } else if (object && typeof object === "string") {
      this.value = object;
      this.searchTerm = object;
    } else {
      this.value = "";
      this.searchTerm = "";
    }
    if (!this.returnRawValue) {
      // Use setTimeout to avoid calling onChange during writeValue from patchValue
      setTimeout(() => {
        this.onChange(this.value);
      }, 0);
    }
  }

// writeValue(value: any): void {
  //   if (value) {
  //     if (typeof value === 'string') {
  //       this.value.key = '';
  //       this.value.label = value;
  //     } else if (typeof value === 'object' && value !== null) {
  //       this.value = value
  //     }
  //   } else {
  //     this.value.key = '';
  //     this.value.label = '';
  //   }
  // }

  // get searchTerm() {
  //   return this.value.label;
  // }

  // When value changes in the form control
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // On blur or touch event
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // If the form control is disabled
  setDisabledState(isDisabled: boolean): void {
    this.disable = isDisabled;
  }

  fetchSuggestionsV2() {

  }

  fetchSuggestions() {
    const searchTerm = this.searchTerm.trim();

    if (this.clientSideSearch) {
      this.suggestions = this.getClientSideFilteredResults(searchTerm);
      this.showSuggestions = true;
      this.canPullMoreData = false;
      this.loading = false;
      this.activeOptionIndex = this.suggestions.length > 0 ? 0 : -1;

      // For AG Grid, ensure overlay is properly displayed
      this.forceOverlayDisplay();
    } else {
      this.loading = true;
      const payload = this.getRequestPayload(searchTerm);
      this.apiService.getSuggestions(this.apiUrl, payload).subscribe({
        next: (res) => {
          const newRecords = res.data.records || [];
          this.applySuggestionPage(newRecords);

          // For AG Grid, ensure overlay is properly displayed
          this.forceOverlayDisplay();
          this.ensureScrollableOrLoadMore();
        },
        error: (err) => {
          this.toasterService.error(err.error.errorDesc);
          console.error("Error fetching suggestions:", err);
          this.loading = false;
        }
      });
    }

  }

  private setSelectedItems() {
    this.selectedItems.forEach((item: any, key: string) => {
      const record = this.suggestions.find(suggestion => suggestion[this.bindValue] == key);
      if (record) {
        record.selected = true;
        this.selectedItems.set(key, record);
      }
    });
    if (this.multiSelect) {
      this.updateMultiSelectValues();
    }
  }

  getRequestPayload(searchTerm: string = this.searchTerm): TableListRequest {
    // determinants search where search string is required in the expression
    if (this.searchUsingExpression) {
      return {
        orderBy: this.orderBy,
        limit: this.limit,
        page: this.page,
        where: {
          filterConditions: [],
          expression: searchTerm
        }
      };
    }

    const payload: TableListRequest = {
      selectFields: this.selectFields,
      orderBy: this.orderBy,
      limit: this.limit,
      page: this.page
    };

    const filterConditions: FilterCondition[] = this.filterByKeys.map((field) => ({
      name: field,
      alias: field,
      operator: "CONTAINS",
      value: [searchTerm]
    }));

    console.log(filterConditions);

    let expression = filterConditions
      .map(fc => fc.alias)
      .join(" OR ");

    if (this.skipIds.length > 0) {
      filterConditions.push({
        name: "id",
        alias: "skipCondition",
        operator: "NOT_IN",
        value: this.skipIds
      });
      expression = `(${expression}) AND skipCondition`;
    }
    console.log("this.additionalFilters: ", this.additionalFilters);
    if (this.additionalFilters && this.additionalFilters.length > 0) {
      const filterExpressions: string[] = [];
      this.additionalFilters.forEach((filter: any) => {
        filterConditions.push(filter);
        filterExpressions.push(filter.alias);
      });
      expression = expression ? `(${expression}) AND (${filterExpressions.join(" AND ")})` : `(${filterExpressions.join(" AND ")})`;
    }

    payload.where = {
      filterConditions: filterConditions,
      expression: expression
    };
    return payload;
  }


  ngOnInit() {
    // this.writeValue(this.preSelectedValue);
    // this.preSelectedValue = {};
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // filter out skipIds list if it has null, undefined and empty string
    this.skipIds = this.skipIds.filter(
      (item) => item !== null && item !== undefined && item !== ""
    );
    this.searchTermSubject
      .pipe(
        debounceTime(300),
        map((term) => term.trim()),
        distinctUntilChanged(),
        switchMap((searchTerm: string) => {
          if (searchTerm === "") {
            this.loading = false;
            return of({ ...AutocompleteDropdownComponent.EMPTY_SEARCH_RESULT });
          }

          if (this.clientSideSearch) {
            this.loading = false;
            return of({
              term: searchTerm,
              records: this.getClientSideFilteredResults(searchTerm),
              hideSuggestions: false
            });
          }

          this.loading = true;
          return this.apiService
            .getSuggestions(this.apiUrl, this.getRequestPayload(searchTerm))
            .pipe(
              map((res: any) => ({
                term: searchTerm,
                records: Array.isArray(res?.data?.records) ? res.data.records : [],
                hideSuggestions: false
              })),
              catchError((err) => {
                console.error("Error fetching suggestions:", err);
                this.loading = false;
                return of({
                  term: searchTerm,
                  records: [],
                  hideSuggestions: false
                });
              })
            );
        })
      )
      .subscribe((res: { term: string; records: any[]; hideSuggestions: boolean }) => {
        const currentSearchTerm = this.searchTerm.trim();

        if (res.term !== currentSearchTerm) {
          return;
        }

        if (res.hideSuggestions) {
          this.suggestions = [];
          this.canPullMoreData = false;
          this.showSuggestions = false;
          this.activeOptionIndex = -1;
          this.loading = false;
          return;
        }

        const newRecords = res.records;
        this.applySuggestionPage(newRecords);
        this.forceOverlayDisplay();
        this.ensureScrollableOrLoadMore();
      });
  }

  // Handle lazy loading / pagination on scroll
  onScroll() {
    const element = this.resultsContainer?.nativeElement;
    if (!element || this.clientSideSearch || this.loading || !this.canPullMoreData) {
      return;
    }

    const remainingScroll = element.scrollHeight - element.scrollTop - element.clientHeight;
    const isNearBottom = remainingScroll <= AutocompleteDropdownComponent.SCROLL_THRESHOLD_PX;

    if (isNearBottom) {
      this.page++;
      this.fetchSuggestions();
    }
  }

  onWheel(event: WheelEvent): void {
    const element = this.resultsContainer?.nativeElement;
    if (!element) {
      return;
    }

    const atTop = element.scrollTop <= 0;
    const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;

    const scrollingPastTop = atTop && event.deltaY < 0;
    const scrollingPastBottom = atBottom && event.deltaY > 0;

    if (scrollingPastTop || scrollingPastBottom) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  // calculateViewportHeight(resultCount: number): number {
  //   const itemHeight = 50; // Height of each result item in pixels
  //   const visibleItems = Math.min(resultCount, this.maxVisibleItems); // Calculate the number of visible items
  //   return visibleItems * itemHeight; // Set the viewport height dynamically
  // }

  // onScroll(): void {
  //   // Check if we can load more data and the loading is not already in progress
  //   if (this.canPullMoreData && !this.loading) {
  //     this.page++; // Increase the page number
  //     this.fetchSuggestions(); // Fetch more suggestions
  //   }
  // }

  updateSearchTerm() {
    const searchTerm = this.searchTerm.trim();
    this.page = 0;
    this.activeOptionIndex = -1;

    if (searchTerm === "?") {
      return; // wait for second ? before acting
    }
    if (searchTerm === "??") {
      if (!this.allowFullSearch) return; // ?? is not allowed when allowFullSearch is set to false

      this.searchTerm = "";
      this.fetchSuggestions();
      return;
    }

    if (!searchTerm) {

      //Set the value to null only when multiSelect is false
      if (!this.multiSelect) {
        this.value = null;
        this.onChange(null);
      }
      this.searchCleared.emit();
      this.clearSuggestionsState();
      this.searchTermSubject.next("");
    } else {
      this.searchTermSubject.next(searchTerm);
    }
  }

  handleEnter(event: KeyboardEvent) {
    // if (this.searchTerm === "%%%") {
    //   this.searchTerm = ""; // reset searchTerm
    //   this.page = 0;
    //   this.fetchSuggestions();
    // }
  }

  select(result: any) {
    if (!this.multiSelect) {
      this.searchTerm = this.resolveDisplayLabel(result);
      this.handleSelectionChange(result);
    } else {
      this.handleSelectionChangeMultiSelect(result);
    }
    this.validateNow();
    this.gridTableService.commitAndRefocusCell(this.params);
  }

  selectCustomInput() {
    const value = this.searchTerm.trim();
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
    this.showSuggestions = false;
    this.customInputSelected.emit(this.searchTerm);
  }

  // Handle the selection change in the autocomplete
  handleSelectionChange(selectedValue: any): void {
    this.value = selectedValue[this.bindValue];
    this.onChange(this.returnRawValue ? { key: this.value, value: this.searchTerm } : this.value);
    this.onTouched();
    this.showSuggestions = false;
    this.suggestionSelected.emit(selectedValue);
  }

  onInputFocus() {
    this.updateWidth();
    this.focusSearch = true;
    this.activeOptionIndex = this.suggestions.length > 0 ? 0 : -1;
    // if(this.value) {
    //   this.page = 0;
    //   this.fetchSuggestions();
    // }
    // if (this.suggestions.length > 0) {
    //   this.suggestions = [];
    //   this.updateSearchTerm();
    // }
  }

  onInputFocusOut() {
    this.focusSearch = false;
    if (this.showSuggestions) {
      return;
    }

    if ((!this.allowCustomInput && !this.value) || !this.searchTerm.trim() || this.multiSelect) {
      this.onTouched();
      this.searchTerm = "";
    }
  }

  onInputKeydown(event: KeyboardEvent) {
    if (this.multiSelect && event.key === "Backspace" && !this.searchTerm.trim() && this.selectedItems.size > 0) {
      event.preventDefault();
      this.removeLastSelectedItem();
      return;
    }

    if (!this.showSuggestions || this.suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (this.activeOptionIndex < this.suggestions.length - 1) {
        this.activeOptionIndex++;
        this.scrollToActiveOption();
      } else if (this.canPullMoreData && !this.loading) {
        this.page++;
        this.fetchSuggestions();
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (this.activeOptionIndex > 0) {
        this.activeOptionIndex--;
      } else {
        this.activeOptionIndex = this.suggestions.length - 1;
      }
      this.scrollToActiveOption();
    } else if (event.key === "Enter") {
      if (this.activeOptionIndex >= 0 && this.activeOptionIndex < this.suggestions.length) {
        this.select(this.suggestions[this.activeOptionIndex]);
      }
    } else if (event.key === "Escape") {
      this.showSuggestions = false;
    }
  }

  onInputPaste(event: ClipboardEvent) {
    setTimeout(() => {
      this.updateSearchTerm();
    }, 0);
  }

  scrollToActiveOption() {
    setTimeout(() => {
      if (this.optionRows && this.activeOptionIndex >= 0) {
        const option = this.optionRows.toArray()[this.activeOptionIndex];
        if (option && option.nativeElement) {
          option.nativeElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      }
    }, 0);
  }

  handleSelectionChangeMultiSelect(selectedValue: any): void {
    if (this.selectedItems.has(selectedValue[this.bindValue])) {
      this.selectedItems.delete(selectedValue[this.bindValue]);
    } else {
      this.selectedItems.set(selectedValue[this.bindValue], selectedValue);
    }

    this.suggestions.forEach(suggestion => {
      suggestion.selected = this.selectedItems.has(suggestion[this.bindValue]);
    });

    this.updateMultiSelectValues();
    this.suggestionSelected.emit(Array.from(this.selectedItems.values()));
    // this.clearSearchInputAfterMultiSelectSelection();
  }

  private updateMultiSelectValues() {
    this.value = Array.from(this.selectedItems.values());
    this.onChange(
      this.value.map((v: any) => (
        this.returnRawValue ? {
          key: v[this.bindValue],
          value: CommonUtils.getNestedValue(v, this.bindLabel)
        } : v[this.bindValue]
      ))
    );
    this.onTouched();
  }

  removeAllSelections() {
    this.selectedItems.clear();
    this.showSuggestions = false;
    this.updateMultiSelectValues();
    this.validateNow();
    this.suggestionSelected.emit([]);
  }

  private writeValuesForMultiSelect(objects: any[]) {
    this.selectedItems.clear();
    if (objects.length === 0) {
      this.updateMultiSelectValues();
      return;
    }

    for (const obj of objects) {
      if (obj && typeof obj === "object" && obj.hasOwnProperty("key")) {
        // When patched with { key, value } format, use obj.value for the label
        this.selectedItems.set(obj.key, {
          [this.bindValue]: obj.key,
          [this.bindLabel]: obj.value
        });
      } else {
        this.selectedItems.set(obj, null);
      }
    }
    this.updateMultiSelectValues();
  }

  hasError() {
    return this.ngControl?.control?.invalid && this.ngControl.control?.touched && this.ngControl.control?.errors;
  }

  get visiblePills(): Array<{ key: string; value: any }> {
    const items = Array.from(this.selectedItems.entries()).map(([key, value]) => ({ key, value }));
    return items.slice(0, this.maxVisiblePills);
  }

  get hiddenPillsCount(): number {
    return Math.max(0, this.selectedItems.size - this.maxVisiblePills);
  }

  focusInput(): void {
    if (this.searchInput?.nativeElement && !this.disable) {
      this.searchInput.nativeElement.focus();
    }
  }

  protected readonly CommonUtils = CommonUtils;

  formatColumnValue(result: any, column: { key: string; dataType?: string }): string {
    const value = CommonUtils.getNestedValue(result, column.key);
    if (column.dataType) {
      return CommonUtils.formatData(value, column.dataType);
    }
    return value ?? "";
  }

  private resolveDisplayLabel(item: any): string {
    const keys = [this.bindLabel, ...this.bindLabelFallbacks ?? []];
    for (const key of keys) {
      const val = CommonUtils.getNestedValue(item, key);
      if (val !== null && val !== undefined && val !== "") return String(val);
    }
    return "";
  }

  setActiveOption(index: number) {
    this.activeOptionIndex = index;
  }

  private focusInSearchBar() {
    setTimeout(() => {
      this.updateWidth();
      this.searchInput.nativeElement.focus();
      this.searchInput.nativeElement.select();
    }, 0);
  }

  handleInput() {
    this.validateNow();
  }

  private validateNow() {
    this.params?.validate?.();
  }


  //AG Grid Related Methods
  agInit(params: ICellEditorParams): void {
    this.params = params;
    Object.keys(params).forEach((k: string) => {
      if ((this as any).hasOwnProperty(k)) {
        (this as any)[k] = (params as any)[k];
      }
    });


    console.log("Inside agInit params: ", params);
    this.writeValue(params.value);
  }

  getValue(): any {
    if (this.multiSelect) {
      return Array.from(this.selectedItems.values()).length > 0
        ? Array.from(this.selectedItems.values()).map((v: any) => ({
          key: v[this.bindValue],
          value: CommonUtils.getNestedValue(v, this.bindLabel)
        }))
        : [];
    }
    return this.value ? { [this.bindValue]: this.value, [this.bindLabel]: this.searchTerm } : null;
  }

  getValidationElement(): HTMLElement {
    return this.searchInput.nativeElement;
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  afterGuiAttached() {
    this.focusInSearchBar();
  }

  private forceOverlayDisplay() {
    if (!this.params) return;
    this.updateWidth();
    this.cdr.detectChanges();

    // Additional timeout to ensure overlay is properly positioned
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  onOverlayAttached() {
    this.updateWidth();
    this.ensureScrollableOrLoadMore();
  }

  private applySuggestionPage(newRecords: any[]): void {
    if (this.page === 0) {
      this.suggestions = newRecords;
    } else {
      this.suggestions = [...this.suggestions, ...newRecords];
    }

    this.setSelectedItems();
    this.canPullMoreData = !this.clientSideSearch && newRecords.length >= this.limit;
    this.loading = false;
    this.showSuggestions = true;
    this.activeOptionIndex = this.suggestions.length > 0 ? 0 : -1;
  }

  private clearSuggestionsState(): void {
    this.suggestions = [];
    this.canPullMoreData = false;
    this.showSuggestions = false;
    this.loading = false;
    this.activeOptionIndex = -1;
  }

  private clearSearchInputAfterMultiSelectSelection(): void {
    this.searchTerm = "";
    this.page = 0;
    this.previousTerm = "";
    this.clearSuggestionsState();
    this.searchTermSubject.next("");
  }

  private ensureScrollableOrLoadMore(): void {
    if (this.clientSideSearch || this.loading || !this.canPullMoreData || !this.showSuggestions) {
      return;
    }

    setTimeout(() => {
      const element = this.resultsContainer?.nativeElement;
      if (!element || this.loading || !this.canPullMoreData) {
        return;
      }

      const hasVerticalScroll = element.scrollHeight > element.clientHeight + 1;
      if (!hasVerticalScroll) {
        this.page++;
        this.fetchSuggestions();
      }
    }, 0);
  }

  getValidationErrors(): string[] | null {
    return this.gridTableService.validateEditableGrid(this.params.col, this.value);
  }

  private getClientSideFilteredResults(searchTerm: string): any[] {
    const searchField = this.filterByKeys[0] ?? "name";
    const normalizedSearchTerm = searchTerm.toLowerCase();

    return this.clientSearchData.filter((item: any) => {
      const fieldValue = item?.[searchField];
      return String(fieldValue ?? "").toLowerCase().includes(normalizedSearchTerm);
    });
  }

  private removeLastSelectedItem(): void {
    const selectedItemEntries = Array.from(this.selectedItems.entries());
    const lastSelectedItem = selectedItemEntries[selectedItemEntries.length - 1];

    if (!lastSelectedItem) {
      return;
    }

    const [lastSelectedKey] = lastSelectedItem;
    this.selectedItems.delete(lastSelectedKey);
    this.suggestions.forEach((suggestion) => {
      suggestion.selected = this.selectedItems.has(suggestion[this.bindValue]);
    });

    this.updateMultiSelectValues();
    this.validateNow();
    this.suggestionSelected.emit(Array.from(this.selectedItems.values()));
  }
}
